import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import BottomSheet from '@src/components/BottomSheet';
import CustomSelect from '@src/components/CustomSelect';
import ArrowDropDownRounded from '@src/assets/icons/ArrowDropDownRounded.svg';
import { getOperatingSystem } from '@src/utils/os';
import { Box, Divider, Typography } from '@mui/material';
import actions from '@src/redux/actions';
import {
  convertPhoneCountryNumber,
  formatPhoneNumberWithDialCode,
} from '@src/services/phoneNumber';
import { StyledUserOrderInfo } from './index.style';
import Invoice from './Invoice';

const UserOrderInfo = ({
  defaultCodeCountry,
  countryCode,
  isPhoneNumberValid,
  onChangePhoneNumberValid,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dropdownEl = useRef();
  const isMobileScreen = window.innerWidth < 480;
  const { latestOrderByUser } = useSelector((state) => state.user);
  const { user } = useSelector((state) => state.auth);
  const { phoneNumber: phoneNumberState = '' } = useSelector(
    (state) => state.order.userOrder,
  );
  const currentOS = getOperatingSystem();
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentOption, setCurrentOption] = useState(defaultCodeCountry || {});

  const [phoneNumber, setPhoneNumber] = useState(
    latestOrderByUser?.user?.phoneNumber || phoneNumberState,
  );

  const changeHandler = (option) => {
    setCurrentOption(option);
    dispatch(
      actions.order.updateUserOrder({
        dialCode: option?.dialCode,
        phoneNumber: formatPhoneNumberWithDialCode(
          option?.dialCode,
          phoneNumber,
        ),
      }),
    );
  };

  const handleChangePhoneNumber = (e) => {
    if (!isPhoneNumberValid) onChangePhoneNumberValid(true);

    setPhoneNumber(e.target.value);
  };

  const verifyCountryPhoneNumber = () =>
    convertPhoneCountryNumber(phoneNumber, currentOption?.dialCode);

  const handleBlurPhoneCountryNumber = () => {
    if (verifyCountryPhoneNumber()) {
      onChangePhoneNumberValid(true);
      dispatch(
        actions.order.updateUserOrder({
          dialCode: currentOption?.dialCode,
          phoneNumber: formatPhoneNumberWithDialCode(
            currentOption?.dialCode,
            phoneNumber,
          ),
        }),
      );
    } else {
      onChangePhoneNumberValid(false);
      dispatch(actions.order.updateUserOrder({ phoneNumber: '' }));
    }
  };

  const handleClickOutside = useCallback(
    (e) => {
      if (
        showDropdown &&
        e.target.closest('.select-code-country') !== dropdownEl.current
      ) {
        setShowDropdown(false);
      }
    },
    [showDropdown, setShowDropdown, dropdownEl],
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    setCurrentOption(defaultCodeCountry || {});
  }, [defaultCodeCountry]);

  useEffect(() => {
    const data = {};
    if (latestOrderByUser?.user?.phoneNumber) {
      data.phoneNumber = latestOrderByUser?.user?.phoneNumber;
    } else if (user?.phoneNumber) {
      data.phoneNumber = user?.phoneNumber;
    }
    if (user?.firstName || user?.lastName)
      data.user = `${user.lastName || ''} ${user.firstName || ''}`;
    if (user?.email) data.email = user.email;
    dispatch(actions.order.updateUserOrder(data));
  }, []);

  useEffect(() => {
    if (latestOrderByUser?.user?.phoneNumber || user?.phoneNumber) {
      dispatch(
        actions.order.updateUserOrder({
          phoneNumber:
            latestOrderByUser?.user?.phoneNumber || user?.phoneNumber,
        }),
      );
    }
  }, [latestOrderByUser]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (latestOrderByUser?.user?.phoneNumber && currentOption?.dialCode) {
      if (isFirstRender.current) {
        const phone = latestOrderByUser?.user?.phoneNumber;
        setPhoneNumber(phone);
      }
      isFirstRender.current = false;
      dispatch(
        actions.order.updateUserOrder({
          dialCode: currentOption?.dialCode,
        }),
      );
    }
  }, [latestOrderByUser, currentOption?.dialCode]);

  const displayPhoneNumber = () => (
    <div ref={dropdownEl} className="select-code-country">
      <div className="select-phone-country">
        <Box
          onClick={() => setShowDropdown(!showDropdown)}
          className="select-btn-phone-country"
        >
          <div className="info_code">
            {currentOS !== 'Window' ? (
              <span className="country_flag">{currentOption?.emoji}</span>
            ) : (
              <span>{currentOption?.code}</span>
            )}
          </div>
          {currentOption?.dialCode}
          <img src={ArrowDropDownRounded} alt="" />
        </Box>
        <Box className="user-info-phone-country">
          <input
            value={phoneNumber}
            name="phone-number-country"
            id="input-phone-number-payment"
            onBlur={handleBlurPhoneCountryNumber}
            placeholder={t('phoneNumberPlaceholder')}
            className="input-change-country-phone"
            onChange={handleChangePhoneNumber}
          />
        </Box>
      </div>
      {isMobileScreen ? (
        <>
          <BottomSheet
            title={t('chooseCountry')}
            onClose={() => setShowDropdown(false)}
            open={showDropdown}
            isModileWeb
          >
            <CustomSelect
              showDropdown={showDropdown}
              onSetShowDropdown={setShowDropdown}
              data={countryCode}
              isModileWeb
              currentOption={currentOption}
              onChange={changeHandler}
            />
          </BottomSheet>
        </>
      ) : (
        showDropdown && (
          <div className="select-custom">
            <CustomSelect
              showDropdown={showDropdown}
              onSetShowDropdown={setShowDropdown}
              data={countryCode}
              currentOption={currentOption}
              onChange={changeHandler}
            />
          </div>
        )
      )}
    </div>
  );

  return (
    <StyledUserOrderInfo isPhoneNumberValid={isPhoneNumberValid}>
      <Box className="user-info-content">
        <Typography className="user-info-item">
          <span className="user-info-item-label" style={{ paddingTop: '5px' }}>
            {t('phoneNumber')}
          </span>
          <Box className="phone-number-card" data-id="enter-phone-number">
            {displayPhoneNumber()}
            {!isPhoneNumberValid && (
              <Typography
                data-id="form-validation-payment-error"
                data-field="phone-number"
                data-message={
                  !phoneNumber
                    ? t('phoneNumberIsRequired')
                    : t('phoneNumberIsInvalid')
                }
                data-form-name="payment-info"
                className="phone-number-warning"
              >
                {!phoneNumber
                  ? t('phoneNumberIsRequired')
                  : t('phoneNumberIsInvalid')}
              </Typography>
            )}
          </Box>
        </Typography>
        <Invoice />
        <Divider className="divider-invoice-info" />
      </Box>
    </StyledUserOrderInfo>
  );
};

export default UserOrderInfo;
